import { MapPin, Star, MoreVertical, Pencil, Trash2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { Address } from "@/lib/types";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

interface AddressCardProps {
  address: Address;
  onEdit: (address: Address) => void;
  onDelete: (address: Address) => void;
  onSetDefault: (address: Address) => void;
}

export function AddressCard({ address, onEdit, onDelete, onSetDefault }: AddressCardProps) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';

  const governorateName = isRtl 
    ? address.governorate?.nameAr 
    : address.governorate?.nameEn;
  const districtName = isRtl 
    ? address.district?.nameAr 
    : address.district?.nameEn;

  const fullAddress = [
    address.block && `${t("addresses.block")} ${address.block}`,
    address.street && `${t("addresses.street")} ${address.street}`,
    address.avenue && `${t("addresses.avenue")} ${address.avenue}`,
    address.building && `${t("addresses.building")} ${address.building}`,
  ].filter(Boolean).join(", ");

  return (
    <Card className={cn(
      "relative transition-all",
      address.isDefault && "ring-2 ring-primary/50"
    )} data-testid={`card-address-${address.id}`}>
      <CardContent className="p-4" dir={isRtl ? "rtl" : "ltr"}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <div className={cn(
              "w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0",
              address.isDefault ? "bg-primary/10" : "bg-muted"
            )}>
              <MapPin className={cn(
                "w-5 h-5",
                address.isDefault ? "text-primary" : "text-muted-foreground"
              )} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold">{address.label}</span>
                {address.isDefault && (
                  <Badge variant="secondary" className="text-xs">
                    <Star className="w-3 h-3 me-1" />
                    {t("addresses.default")}
                  </Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground mt-1">{fullAddress}</p>
              {address.landmark && (
                <p className="text-xs text-muted-foreground mt-0.5">
                  {t("addresses.landmark")}: {address.landmark}
                </p>
              )}
              <p className="text-xs text-muted-foreground mt-1">
                {districtName}, {governorateName}
              </p>
            </div>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" data-testid={`button-address-menu-${address.id}`}>
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align={isRtl ? "start" : "end"}>
              <DropdownMenuItem onClick={() => onEdit(address)} data-testid={`button-edit-address-${address.id}`}>
                <Pencil className="w-4 h-4 me-2" />
                {t("addresses.edit")}
              </DropdownMenuItem>
              {!address.isDefault && (
                <DropdownMenuItem onClick={() => onSetDefault(address)} data-testid={`button-set-default-${address.id}`}>
                  <Star className="w-4 h-4 me-2" />
                  {t("addresses.setAsDefault")}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem 
                onClick={() => onDelete(address)} 
                className="text-destructive focus:text-destructive"
                data-testid={`button-delete-address-${address.id}`}
              >
                <Trash2 className="w-4 h-4 me-2" />
                {t("addresses.delete")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardContent>
    </Card>
  );
}
